import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { extractErrorMessage } from '../../api/errors';
import { useSyncStripePriceMappings } from '../../queries/stripePriceMappings';

interface SyncWithStripeDialogProps {
  onClose: () => void;
}

export function SyncWithStripeDialog({ onClose }: SyncWithStripeDialogProps) {
  const [forceRecreate, setForceRecreate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ productsCreated: number; pricesCreated: number; mappingsUpdated: number } | null>(null);
  const sync = useSyncStripePriceMappings();

  const handleConfirm = () => {
    setError(null);
    sync.mutate(forceRecreate, {
      onSuccess: (res) => setResult(res),
      onError: (err) => setError(extractErrorMessage(err, 'Sync failed.')),
    });
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Sync with Stripe</Dialog.Title>

          {result ? (
            <div className="mt-4 space-y-2 text-sm">
              <p>Sync complete.</p>
              <ul className="list-disc pl-5 text-muted-foreground">
                <li>{result.productsCreated} Stripe product(s) created</li>
                <li>{result.pricesCreated} Stripe price(s) created</li>
                <li>{result.mappingsUpdated} mapping(s) updated</li>
              </ul>
            </div>
          ) : (
            <>
              <p className="mt-2 text-sm text-muted-foreground">
                For every active package, this retrieves-or-creates a real Stripe product and weekly/monthly prices, and
                updates the mapping to point at them. This creates live Stripe objects.
              </p>

              <label className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/20">
                <Checkbox
                  checked={forceRecreate}
                  onChange={(e) => setForceRecreate(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">Force recreate all prices</span>
                  <br />
                  <span className="text-muted-foreground">
                    Skips reusing existing prices — always creates a brand new Stripe price for every package, even if one
                    is already mapped. Old prices are never deleted (Stripe prices are immutable), but existing customers
                    keep billing on their current price until they change plans.
                  </span>
                </span>
              </label>

              {forceRecreate && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/20">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                  <span className="text-red-800 dark:text-red-300">
                    This will create a new Stripe price for every active package right now, whether or not one already
                    exists. Only do this if you intend to replace current pricing.
                  </span>
                </div>
              )}

              {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
            </>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={sync.isPending}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
                  forceRecreate ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:opacity-90'
                }`}
              >
                {sync.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {forceRecreate ? 'Force recreate & sync' : 'Sync'}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
